export const PROTECTED_FIELDS = ["id", "_id", "created_at", "created_by", "updated_at", "updated_by", "deleted_at", "is_demo"];

export function publicUrlFor(entity, row) {
  if (!row) return null;
  switch (entity) {
    case "services": return `/services/${row.slug || row.id}`;
    case "provider_profiles": return `/providers/${row.slug || row.id}`;
    case "project_requests": return row.flow === "smart" ? `/admin/service-requests/${row.id}` : `/requests/${row.id}`;
    case "categories": return `/providers?category_id=${row.id}`;
    case "bookings": return `/dashboard/bookings/${row.id}`;
    case "orders": return `/dashboard/orders/${row.id}`;
    case "disputes": return `/dashboard/disputes/${row.id}`;
    case "support_tickets": return `/dashboard/support/${row.id}`;
    default: return null;
  }
}

const ACRONYMS = { id: "ID", url: "URL", ein: "EIN", zip: "ZIP", sku: "SKU", seo: "SEO", avg: "avg" };
export const humanize = (f) => String(f).replace(/^-/, "").split("_").map((w, i) => ACRONYMS[w] || (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");

export const singular = (label) => String(label).replace(/ies$/i, "y").replace(/(ss|sh|ch|x)es$/i, "$1").replace(/([^s])s$/i, "$1");
const tokens = (f) => String(f).toLowerCase().split("_");
const hasToken = (f, list) => tokens(f).some((t) => list.includes(t));

export const TITLE_KEYS = ["title", "display_name", "business_name", "name", "service_title", "subject", "legal_name", "email", "code", "key", "label"];
export const titleOf = (row) => {
  const k = TITLE_KEYS.find((t) => row?.[t]);
  return k ? String(row[k]) : `Record ${String(row?.id || "").slice(0, 8)}`;
};

export const isMediaField = (f) => /(image|photo|picture|avatar|logo|cover|hero|thumbnail|banner|gallery)/i.test(f) && !/icon/i.test(f);
export const isMoneyField = (f) => /(price|amount|fee|cost|budget|total|payout|earning|balance|deposit|estimate)/i.test(f) || hasToken(f, ["rate", "hourly"]);
const isNumberName = (f) => isMoneyField(f) || hasToken(f, ["hours", "minutes", "days", "count", "percent", "qty", "quantity", "min", "max", "order", "radius", "miles", "capacity", "guests", "bedrooms", "bathrooms", "value", "lat", "lng"]);
export const isDateField = (f, v) => /(_at|_on|_date|^date|_start|_end|starts|ends|expires|renews)/i.test(f) || (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v));
const isRefField = (f) => f === "id" || /_id$|_ids$|^ext_|_by$/.test(f);
const isLongText = (f, v) => /(description|bio|about|notes|summary|body|content|message|details|reason|policy|terms)/i.test(f) || String(v ?? "").length > 90;

export const GROUPS = [
  { key: "basics", label: "Basics", hint: "Names, titles and the main description." },
  { key: "media", label: "Photos & media", hint: "Images shown publicly." },
  { key: "pricing", label: "Pricing & amounts", hint: "Prices, fees and totals." },
  { key: "contact", label: "Contact & location", hint: "How and where to reach them." },
  { key: "schedule", label: "Dates & schedule", hint: "Start, end and expiry dates." },
  { key: "settings", label: "Visibility & settings", hint: "Switches that control how this record behaves." },
  { key: "advanced", label: "Advanced", hint: "Linked IDs and technical data. Edit with care." },
];

export function groupOf(f, v) {
  if (isRefField(f)) return "advanced";
  if (isMediaField(f)) return "media";
  if (v !== null && typeof v === "object" && !Array.isArray(v)) return "advanced";
  if (Array.isArray(v) && v.some((x) => x && typeof x === "object")) return "advanced";
  if (typeof v === "boolean") return "settings";
  if (isMoneyField(f)) return "pricing";
  if (hasToken(f, ["email", "phone", "address", "street", "city", "state", "zip", "country", "lat", "lng", "lon", "website", "location", "region", "county", "mobile"])) return "contact";
  if (isDateField(f, v)) return "schedule";
  if (hasToken(f, ["featured", "visibility", "visible", "active", "published", "verified", "sort", "priority", "rank", "kind", "type", "mode", "tier", "plan", "role"])) return "settings";
  return "basics";
}

export function fieldKind(f, v) {
  if (typeof v === "boolean") return "bool";
  if (isMediaField(f) && (typeof v === "string" || v == null)) return "media";
  if (Array.isArray(v) && v.every((x) => typeof x === "string" || typeof x === "number")) return "tags";
  if (v !== null && typeof v === "object") return "json";
  if (typeof v === "number" || (v == null && isNumberName(f))) return "number";
  if (isDateField(f, v) && (v == null || typeof v === "string")) return "date";
  if (isLongText(f, v)) return "long";
  return "text";
}

export function defaultColumns(meta) {
  const all = (meta?.list_fields || []).filter((f) => !["id", "slug", "email"].includes(f) && !TITLE_KEYS.slice(0, 7).includes(f));
  const status = meta?.status_field;
  const rest = all.filter((f) => f !== status && f !== "created_at");
  rest.sort((a, b) => Number(isRefField(a)) - Number(isRefField(b)));
  const picked = [...(status && all.includes(status) ? [status] : []), ...rest.slice(0, 3), ...(all.includes("created_at") ? ["created_at"] : [])];
  return picked.slice(0, 5);
}

export function allColumnOptions(meta, items) {
  const set = new Set([...(meta?.list_fields || [])]);
  (items || []).slice(0, 10).forEach((r) => Object.keys(r || {}).forEach((k) => { if (k !== "_id") set.add(k); }));
  return [...set].filter((f) => !["_id", "id", "created_by", "updated_by", "is_demo", "deleted_at"].includes(f));
}

export function fmtDate(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export const STATUS_TONE = (s) => {
  const v = String(s || "").toLowerCase();
  if (/(active|approved|published|paid|completed|verified|succeeded|resolved|live)/.test(v)) return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
  if (/(pending|review|draft|requested|open|scheduled|processing|in_progress|awaiting)/.test(v)) return "bg-amber-50 text-amber-700 ring-amber-600/20";
  if (/(rejected|cancel|suspend|banned|failed|declined|expired|disputed|refund|deleted)/.test(v)) return "bg-rose-50 text-rose-700 ring-rose-600/20";
  return "bg-slate-100 text-slate-700 ring-slate-500/20";
};
